import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { AXIOS_INSTANCE } from '@/shared/api/mutator/custom-instance';
import * as axios from 'axios';
import {
    SelfRegistrationSessionDTO,
    SelfRegistrationRequestDTO,
    SelfRegistrationStepRequestDTO,
    RequiredDocumentDTO,
    UploadedDocumentDTO,
    OfficerReviewRequestDTO,
    DepositRequestDTO,
    DepositUpdateRequestDTO
} from '@/shared/api/model';
import {
    useInitiateRegistration,
    useVerifyPhone,
    useResendPhoneOtp,
    useAcceptTerms,
    useInitiateEmailVerification,
    useVerifyEmail,
    useResendEmailOtp,
    useUpdatePersonalInfo,
    useUpdateLocationInfo,
    useGetRequiredDocuments,
    useUploadDocument,
    useGetSession,
    useCompleteRegistration,
    useProcessDeposit,
    useUpdateDepositInfo,
    useCompleteDeposit,
    useSubmitDirectDepositEvidence,
    useApproveSession,
    useRejectSession,
    useGetPendingReviews
} from '@/shared/api/client/self-registration';

interface UseSelfRegistrationOptions {
    searchName?: string;
    searchNidaId?: string;
    searchEmail?: string;
    enablePendingReviews?: boolean;
}

export const useSelfRegistration = (options: UseSelfRegistrationOptions = {}) => {
    const {
        searchName,
        searchNidaId,
        searchEmail,
        enablePendingReviews = false
    } = options;
    const queryClient = useQueryClient();
    const [error, setError] = useState<string | null>(null);

    // Mutation hooks using Orval-generated client functions
    const initiateRegistrationMutation = useInitiateRegistration();

    const verifyPhoneMutation = useVerifyPhone();

    const resendPhoneOtpMutation = useResendPhoneOtp();

    const acceptTermsMutation = useAcceptTerms({
        mutation: {
            onError: (error: any) => {
                console.error('Error accepting terms:', error);
                setError('Failed to accept terms');
            }
        }
    });

    const initiateEmailVerificationMutation = useInitiateEmailVerification({
        mutation: {
            onError: (error: any) => {
                console.error('Error initiating email verification:', error);
                setError('Failed to initiate email verification');
            }
        }
    });

    const verifyEmailMutation = useVerifyEmail({
        mutation: {
            onError: (error: any) => {
                console.error('Error verifying email:', error);
                setError('Failed to verify email');
            }
        }
    });

    const resendEmailOtpMutation = useResendEmailOtp({
        mutation: {
            onError: (error: any) => {
                console.error('Error resending email OTP:', error);
                setError('Failed to resend email OTP');
            }
        }
    });

    const updatePersonalInfoMutation = useUpdatePersonalInfo({
        mutation: {
            onError: (error: any) => {
                console.error('Error updating personal info:', error);
                setError('Failed to update personal information');
            }
        }
    });

    const updateLocationInfoMutation = useUpdateLocationInfo({
        mutation: {
            onError: (error: any) => {
                console.error('Error updating location info:', error);
                setError('Failed to update location information');
            }
        }
    });

    const uploadDocumentMutation = useUploadDocument({
        mutation: {
            onError: (error: any) => {
                console.error('Error uploading document:', error);
                setError('Failed to upload document');
            }
        }
    });

    const completeRegistrationMutation = useCompleteRegistration({
        mutation: {
            onError: (error: any) => {
                console.error('Error completing registration:', error);
                setError('Failed to complete registration');
            }
        }
    });

    const processDepositMutation = useProcessDeposit({
        mutation: {
            onError: (error: any) => {
                console.error('Error processing deposit:', error);
                setError('Failed to process deposit');
            }
        }
    });

    const updateDepositInfoMutation = useUpdateDepositInfo({
        mutation: {
            onSuccess: () => {
                message.success('Deposit information updated successfully');
                queryClient.invalidateQueries({ queryKey: ['/api/v1/self-registration'] });
            },
            onError: (error: any) => {
                console.error('Error updating deposit info:', error);
                message.error('Failed to update deposit information');
                setError('Failed to update deposit information');
            }
        }
    });

    const completeDepositMutation = useCompleteDeposit({
        mutation: {
            onError: (error: any) => {
                console.error('Error completing deposit:', error);
                setError('Failed to complete deposit');
            }
        }
    });

    const submitDirectDepositEvidenceMutation = useSubmitDirectDepositEvidence({
        mutation: {
            onError: (error: any) => {
                const errorMessage = error.response?.data?.message || 'Failed to submit direct deposit evidence.';
                console.error('Error submitting direct deposit evidence:', error);
                setError(errorMessage);
            }
        }
    });

    // Officer Review functionality - using Orval-generated hooks
    const pendingReviewsQuery = useGetPendingReviews(
        {
            name: searchName || undefined,
            nidaId: searchNidaId || undefined,
            email: searchEmail || undefined,
        },
        {
            query: {
                enabled: enablePendingReviews,
                staleTime: 30 * 1000, // 30 seconds
                gcTime: 2 * 60 * 1000, // 2 minutes
            }
        }
    );

    const approveSessionMutation = useApproveSession({
        mutation: {
            onSuccess: () => {
                message.success('Application approved successfully');
                queryClient.invalidateQueries({ queryKey: ['/frontend/v1/self-registration/pending-reviews'] });
                queryClient.invalidateQueries({ queryKey: ['/api/v1/self-registration'] });
            },
            onError: (error: any) => {
                const errorMessage = error.response?.data?.message || 'Failed to approve application';
                console.error('Error approving session:', error);
                message.error(errorMessage);
                setError(errorMessage);
            }
        }
    });

    const rejectSessionMutation = useRejectSession({
        mutation: {
            onSuccess: () => {
                message.success('Application rejected successfully');
                queryClient.invalidateQueries({ queryKey: ['/frontend/v1/self-registration/pending-reviews'] });
                queryClient.invalidateQueries({ queryKey: ['/api/v1/self-registration'] });
            },
            onError: (error: any) => {
                const errorMessage = error.response?.data?.message || 'Failed to reject application';
                console.error('Error rejecting session:', error);
                message.error(errorMessage);
                setError(errorMessage);
            }
        }
    });


    // Query hooks - only fetch required documents when we have a session
    const requiredDocumentsQuery = useGetRequiredDocuments({
        query: {
            enabled: false, // Disable automatic fetching - we'll fetch manually when needed
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes (React Query v5 uses gcTime instead of cacheTime)
        }
    });

    // Helper function to extract error message from API response
    const extractErrorMessage = (error: any): string => {
        // Try to extract the actual error message from the backend response
        if (error?.response?.data?.message) {
            return error.response.data.message;
        }
        if (error?.response?.data?.error) {
            return error.response.data.error;
        }
        if (error?.message) {
            return error.message;
        }
        return 'An unexpected error occurred';
    };

    const unwrapApiResponse = <T>(response: any): T => {
        // customInstance spreads the response body AND sets .data = response body
        // So response.data = ApiResponseDTO { success, data: T }
        // We need response.data.data to get the actual DTO
        const first = response?.data ?? response;
        return (first?.data ?? first) as T;
    };

    // Action functions that use the mutations
    const initiateRegistration = (request: SelfRegistrationRequestDTO): Promise<SelfRegistrationSessionDTO> => {
        return new Promise((resolve, reject) => {
            initiateRegistrationMutation.mutate({ data: request }, {
                onSuccess: (response) => {
                    resolve(unwrapApiResponse<SelfRegistrationSessionDTO>(response));
                },
                onError: (error: any) => {
                    console.error('Error initiating registration:', error);
                    const errorMessage = extractErrorMessage(error);
                    setError(errorMessage);
                    reject(new Error(errorMessage));
                }
            });
        });
    };

    const verifyPhone = (request: SelfRegistrationStepRequestDTO): Promise<SelfRegistrationSessionDTO> => {
        return new Promise((resolve, reject) => {
            verifyPhoneMutation.mutate({ data: request }, {
                onSuccess: (response) => {
                    // Don't show success message here - let the component handle it
                    resolve(unwrapApiResponse<SelfRegistrationSessionDTO>(response));
                },
                onError: (error: any) => {
                    console.error('Error verifying phone:', error);
                    const errorMessage = extractErrorMessage(error);
                    setError(errorMessage);
                    reject(new Error(errorMessage));
                }
            });
        });
    };

    const resendPhoneOtp = (sessionToken: string): Promise<SelfRegistrationSessionDTO> => {
        return new Promise((resolve, reject) => {
            resendPhoneOtpMutation.mutate({ params: { sessionToken } }, {
                onSuccess: (response) => {
                    resolve(unwrapApiResponse<SelfRegistrationSessionDTO>(response));
                },
                onError: (error: any) => {
                    console.error('Error resending phone OTP:', error);
                    const errorMessage = extractErrorMessage(error);
                    setError(errorMessage);
                    reject(new Error(errorMessage));
                }
            });
        });
    };

    const acceptTerms = (request: SelfRegistrationStepRequestDTO): Promise<SelfRegistrationSessionDTO> => {
        return new Promise((resolve, reject) => {
            acceptTermsMutation.mutate({ data: request }, {
                onSuccess: (response) => {
                    console.log('✅ Terms accepted successfully, session data:', response);
                    resolve(unwrapApiResponse<SelfRegistrationSessionDTO>(response));
                },
                onError: (error) => {
                    console.error('❌ Error accepting terms:', error);
                    const errorMessage = extractErrorMessage(error);
                    setError(errorMessage);
                    reject(new Error(errorMessage));
                }
            });
        });
    };

    const initiateEmailVerification = async (sessionToken: string, email: string): Promise<void> => {
        try {
            await initiateEmailVerificationMutation.mutateAsync({ params: { sessionToken, email } });
            console.log('✅ Email verification initiated successfully');
        } catch (error) {
            console.error('❌ Error initiating email verification:', error);
            const errorMessage = extractErrorMessage(error);
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const verifyEmail = async (request: SelfRegistrationStepRequestDTO): Promise<SelfRegistrationSessionDTO> => {
        try {
            const response = await verifyEmailMutation.mutateAsync({ data: request });
            console.log('✅ Email verified successfully, session data:', response);
            return unwrapApiResponse<SelfRegistrationSessionDTO>(response);
        } catch (error) {
            console.error('❌ Error verifying email:', error);
            const errorMessage = extractErrorMessage(error);
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const resendEmailOtp = async (sessionToken: string): Promise<void> => {
        try {
            await resendEmailOtpMutation.mutateAsync({ params: { sessionToken } });
            console.log('✅ Email OTP resent successfully');
        } catch (error) {
            console.error('❌ Error resending email OTP:', error);
            const errorMessage = extractErrorMessage(error);
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const updatePersonalInfo = async (request: SelfRegistrationStepRequestDTO): Promise<SelfRegistrationSessionDTO> => {
        try {
            const response = await updatePersonalInfoMutation.mutateAsync({ data: request });
            console.log('✅ Personal info updated successfully, session data:', response);
            return unwrapApiResponse<SelfRegistrationSessionDTO>(response);
        } catch (error) {
            console.error('❌ Error updating personal info:', error);
            const errorMessage = extractErrorMessage(error);
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const uploadDocument = async (sessionToken: string, documentType: string, file: File): Promise<any> => {
        console.log('🔄 Uploading document with params:', { sessionToken, documentType, fileName: file.name });
        try {
            const response = await uploadDocumentMutation.mutateAsync({
                data: { file: file },
                params: { sessionToken, documentType }
            });
            console.log('✅ Document uploaded successfully:', response);
            return unwrapApiResponse(response);
        } catch (error: any) {
            console.error('❌ Error uploading document:', error);
            const errorMessage = extractErrorMessage(error);
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const completeRegistration = async (sessionToken: string): Promise<SelfRegistrationSessionDTO> => {
        try {
            const response = await completeRegistrationMutation.mutateAsync({ params: { sessionToken } });
            console.log('✅ Registration completed successfully:', response);
            return unwrapApiResponse<SelfRegistrationSessionDTO>(response);
        } catch (error: any) {
            console.error('❌ Error completing registration:', error);
            const errorMessage = extractErrorMessage(error);
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const completeManagementRegistration = (sessionToken: string): Promise<SelfRegistrationSessionDTO> => {
        return new Promise((resolve, reject) => {
            axios.default.post('/frontend/v1/self-registration/management/complete', undefined, {
                params: { sessionToken },
            }).then((response) => {
                console.log('✅ Management registration completed successfully:', response.data);
                message.success('Registration completed successfully');
                queryClient.invalidateQueries({ queryKey: ['/api/v1/self-registration'] });
                resolve(response.data?.data as SelfRegistrationSessionDTO);
            }).catch((error) => {
                console.error('❌ Error completing management registration:', error);
                message.error('Failed to complete registration');
                setError('Failed to complete registration');
                reject(error);
            });
        });
    };

    const processDeposit = async (sessionToken: string, request: DepositRequestDTO): Promise<SelfRegistrationSessionDTO> => {
        try {
            const response = await processDepositMutation.mutateAsync({ sessionToken, data: request });
            console.log('✅ Deposit processed successfully:', response);
            return unwrapApiResponse<SelfRegistrationSessionDTO>(response);
        } catch (error: any) {
            console.error('❌ Error processing deposit:', error);
            const errorMessage = extractErrorMessage(error);
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const updateDepositInfo = async (sessionToken: string, request: DepositUpdateRequestDTO): Promise<SelfRegistrationSessionDTO> => {
        try {
            const response = await updateDepositInfoMutation.mutateAsync({ sessionToken, data: request });
            console.log('✅ Deposit info updated successfully:', response);
            return unwrapApiResponse<SelfRegistrationSessionDTO>(response);
        } catch (error: any) {
            console.error('❌ Error updating deposit info:', error);
            const errorMessage = extractErrorMessage(error);
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const completeDeposit = async (sessionToken: string): Promise<SelfRegistrationSessionDTO> => {
        try {
            const response = await completeDepositMutation.mutateAsync({ sessionToken });
            console.log('✅ Deposit completed successfully:', response);
            return unwrapApiResponse<SelfRegistrationSessionDTO>(response);
        } catch (error: any) {
            console.error('❌ Error completing deposit:', error);
            const errorMessage = extractErrorMessage(error);
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    interface SubmitDirectDepositEvidenceParams {
        sessionToken: string;
        amount: number;
        paymentDate: string;
        file: File;
    }

    const submitDirectDepositEvidence = (params: SubmitDirectDepositEvidenceParams): Promise<SelfRegistrationSessionDTO> => {
        const { sessionToken, amount, paymentDate, file } = params;
        return new Promise((resolve, reject) => {
            // Create evidence object
            const evidenceObject = {
                sessionToken: sessionToken,
                amount: amount,
                paymentDate: paymentDate
            };

            // Format data according to the Orval-generated client expectations
            // Backend expects evidence as JSON string
            const submitData = {
                sessionToken: sessionToken,
                evidence: JSON.stringify(evidenceObject), // Convert to JSON string
                file: file
            };

            console.log('🔄 Submitting direct deposit evidence with data:', {
                sessionToken,
                evidence: evidenceObject,
                evidenceJson: JSON.stringify(evidenceObject),
                fileName: file.name
            });

            submitDirectDepositEvidenceMutation.mutateAsync({
                data: submitData
            }).then((response) => {
                console.log('✅ Direct deposit evidence submission successful:', response);
                resolve(unwrapApiResponse<SelfRegistrationSessionDTO>(response));
            }).catch((error) => {
                console.error('❌ Direct deposit evidence submission failed:', error);
                const errorMessage = extractErrorMessage(error);
                setError(errorMessage);
                reject(new Error(errorMessage));
            });
        });
    };

    // Get session function
    const getSession = (sessionToken: string | null) => {
        return useGetSession(sessionToken || '', {
            query: {
                enabled: !!sessionToken,
                select: (response: any) => unwrapApiResponse<SelfRegistrationSessionDTO>(response),
                refetchInterval: 30000, // Refetch every 30 seconds
                staleTime: 10000 // Consider data stale after 10 seconds
            }
        });
    };

    const getCurrentSession = async (): Promise<SelfRegistrationSessionDTO | null> => {
        try {
            const response = await AXIOS_INSTANCE.get('/frontend/v1/self-registration/session/current');
            return unwrapApiResponse<SelfRegistrationSessionDTO>(response.data);
        } catch (error: any) {
            if (error?.response?.status === 404) {
                return null;
            }
            throw error;
        }
    };

    const exitRegistration = async (): Promise<void> => {
        await AXIOS_INSTANCE.post('/frontend/v1/self-registration/session/exit');
    };

    // Refresh data
    const refreshData = () => {
        queryClient.invalidateQueries({ queryKey: ['/api/v1/self-registration'] });
    };

    // Fetch required documents manually
    const fetchRequiredDocuments = () => {
        console.log('🔄 Manually fetching required documents...');
        return requiredDocumentsQuery.refetch();
    };

    return {
        // Data
        requiredDocuments: requiredDocumentsQuery.data?.data,
        error,

        // Loading states
        isInitiating: initiateRegistrationMutation.isPending,
        isVerifyingPhone: verifyPhoneMutation.isPending,
        isResendingPhoneOtp: resendPhoneOtpMutation.isPending,
        isAcceptingTerms: acceptTermsMutation.isPending,
        isInitiatingEmailVerification: initiateEmailVerificationMutation.isPending,
        isVerifyingEmail: verifyEmailMutation.isPending,
        isResendingEmailOtp: resendEmailOtpMutation.isPending,
        isUpdatingPersonalInfo: updatePersonalInfoMutation.isPending,
        isUpdatingLocationInfo: updateLocationInfoMutation.isPending,
        isUploadingDocument: uploadDocumentMutation.isPending,
        isCompletingRegistration: completeRegistrationMutation.isPending,
        isProcessingDeposit: processDepositMutation.isPending,
        isUpdatingDepositInfo: updateDepositInfoMutation.isPending,
        isCompletingDeposit: completeDepositMutation.isPending,
        isSubmittingDirectDeposit: submitDirectDepositEvidenceMutation.isPending,
        isLoadingRequiredDocuments: requiredDocumentsQuery.isLoading,


        // Actions
        initiateRegistration,
        verifyPhone,
        resendPhoneOtp,
        acceptTerms,
        initiateEmailVerification,
        verifyEmail,
        resendEmailOtp,
        updatePersonalInfo,
        updateLocationInfo: async (sessionToken: string, locationDataJson: string): Promise<SelfRegistrationSessionDTO> => {
            const request: SelfRegistrationStepRequestDTO = {
                sessionToken: sessionToken,
                locationDataJson: locationDataJson
            };
            try {
                const response = await updateLocationInfoMutation.mutateAsync({ data: request });
                console.log('✅ Location info updated successfully:', response);
                return unwrapApiResponse<SelfRegistrationSessionDTO>(response);
            } catch (error: any) {
                console.error('❌ Error updating location info:', error);
                const errorMessage = extractErrorMessage(error);
                setError(errorMessage);
                throw new Error(errorMessage);
            }
        },
        uploadDocument,
        completeRegistration,
        completeManagementRegistration,
        processDeposit,
        updateDepositInfo,
        completeDeposit,
        submitDirectDepositEvidence,

        // Officer Review functions
        approveSession: (params: {
            sessionToken: string;
            officerId: string;
            comments: string;
            monthlyDeductionAmount?: number;
            employerId?: number;
            newEmployerName?: string;
            newEmployerVoteNumber?: string;
            newEmployerAddress?: string;
            newEmployerPoBox?: string;
            newEmployerPhone?: string;
            newEmployerEmail?: string;
            officerInitials?: string;
            officerName?: string;
            officerPhone?: string;
        }) => {
            return new Promise<void>((resolve, reject) => {
                const requestData: OfficerReviewRequestDTO = {
                    sessionToken: params.sessionToken,
                    officerId: params.officerId,
                    comments: params.comments,
                    action: 'APPROVE'
                };

                const extendedRequestData = {
                    ...requestData,
                    monthlyDeductionAmount: params.monthlyDeductionAmount,
                    employerId: params.employerId,
                    newEmployerName: params.newEmployerName,
                    newEmployerVoteNumber: params.newEmployerVoteNumber,
                    newEmployerAddress: params.newEmployerAddress,
                    newEmployerPoBox: params.newEmployerPoBox,
                    newEmployerPhone: params.newEmployerPhone,
                    newEmployerEmail: params.newEmployerEmail,
                    officerInitials: params.officerInitials,
                    officerName: params.officerName,
                    officerPhone: params.officerPhone
                };

                approveSessionMutation.mutate({ data: extendedRequestData as any }, {
                    onSuccess: () => resolve(),
                    onError: (error) => reject(error)
                });
            });
        },
        rejectSession: (params: { sessionToken: string; officerId: string; rejectionReason: string }) => {
            return new Promise<void>((resolve, reject) => {
                const requestData: OfficerReviewRequestDTO = {
                    sessionToken: params.sessionToken,
                    officerId: params.officerId,
                    rejectionReason: params.rejectionReason,
                    action: 'REJECT'
                };
                rejectSessionMutation.mutate({ data: requestData }, {
                    onSuccess: () => resolve(),
                    onError: (error) => reject(error)
                });
            });
        },

        // Officer Review data and states
        pendingReviews: pendingReviewsQuery.data?.data ?? pendingReviewsQuery.data,
        isLoadingPendingReviews: pendingReviewsQuery.isLoading,
        isApprovingSession: approveSessionMutation.isPending,
        isRejectingSession: rejectSessionMutation.isPending,

        getSession,
        getCurrentSession,
        exitRegistration,
        refreshData,
        fetchRequiredDocuments,
        setError
    };
};

// Utility hook for countdown timer (used in OTP resend)
export const useCountdown = (initialTime: number) => {
    const [timeLeft, setTimeLeft] = React.useState(initialTime);
    const [isActive, setIsActive] = React.useState(false);

    React.useEffect(() => {
        let intervalId: NodeJS.Timeout;

        if (isActive && timeLeft > 0) {
            intervalId = setInterval(() => {
                setTimeLeft(time => time - 1);
            }, 1000);
        } else if (timeLeft === 0) {
            setIsActive(false);
        }

        return () => clearInterval(intervalId);
    }, [isActive, timeLeft]);

    const startCountdown = (time?: number) => {
        if (time) setTimeLeft(time);
        setIsActive(true);
    };

    const stopCountdown = () => {
        setIsActive(false);
        setTimeLeft(0);
    };

    return {
        timeLeft,
        isActive,
        startCountdown,
        stopCountdown
    };
};
